import { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Home, Check, X, Loader2, RefreshCw, Crown, UserPlus, Trash2, Pause, Play, Globe, LogOut } from "lucide-react";
import { API, AuthContext } from "../App";

/**
 * Admin section for managing households / family mode.
 * - Reviews pending household requests
 * - Lists active / suspended households
 * - Direct-create, suspend, reactivate, delete, add member by username
 */
const AdminHouseholdsPanel = () => {
  const { token } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [loadingReq, setLoadingReq] = useState(true);
  const [loadingHh, setLoadingHh] = useState(true);
  const [busy, setBusy] = useState(false);

  // Direct-create form
  const [newOwner, setNewOwner] = useState("");
  const [newName, setNewName] = useState("");

  // Add-member-by-username per household
  const [addMemberFor, setAddMemberFor] = useState(null);
  const [addMemberUsername, setAddMemberUsername] = useState("");

  const auth = { Authorization: `Bearer ${token}` };

  const fetchRequests = useCallback(async () => {
    setLoadingReq(true);
    try {
      const res = await axios.get(`${API}/admin/households/requests`, { headers: auth, params: { status: "pending" } });
      setRequests(res.data.requests || []);
    } catch (err) {
      console.error(err);
    } finally { setLoadingReq(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHouseholds = useCallback(async () => {
    setLoadingHh(true);
    try {
      const res = await axios.get(`${API}/admin/households`, { headers: auth });
      setHouseholds(res.data.households || []);
    } catch (err) {
      console.error(err);
    } finally { setLoadingHh(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRequests(); fetchHouseholds(); }, [fetchRequests, fetchHouseholds]);

  const handleErr = (err, fallback) => {
    const d = err?.response?.data?.detail;
    toast.error(Array.isArray(d) ? (d[0]?.msg || fallback) : (d || fallback));
  };

  const approveRequest = async (request_id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/households/requests/${request_id}/approve`, {}, { headers: auth });
      toast.success(r.data?.message || "Approved");
      fetchRequests(); fetchHouseholds();
    } catch (err) { handleErr(err, "Approve failed"); }
    finally { setBusy(false); }
  };

  const denyRequest = async (request_id) => {
    if (!window.confirm("Deny this household request?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/households/requests/${request_id}/deny`, {}, { headers: auth });
      toast.success("Denied");
      fetchRequests();
    } catch (err) { handleErr(err, "Deny failed"); }
    finally { setBusy(false); }
  };

  const directCreate = async () => {
    if (!newOwner.trim() || !newName.trim()) { toast.error("Owner username and household name are required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/households/create`, { owner_username: newOwner.trim(), name: newName.trim() }, { headers: auth });
      toast.success(r.data?.message || "Created");
      setNewOwner(""); setNewName("");
      fetchHouseholds();
    } catch (err) { handleErr(err, "Create failed"); }
    finally { setBusy(false); }
  };

  const suspend = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/households/${id}/suspend`, {}, { headers: auth });
      toast.success("Suspended");
      fetchHouseholds();
    } catch (err) { handleErr(err, "Suspend failed"); }
    finally { setBusy(false); }
  };

  const reactivate = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/households/${id}/reactivate`, {}, { headers: auth });
      toast.success("Reactivated");
      fetchHouseholds();
    } catch (err) { handleErr(err, "Reactivate failed"); }
    finally { setBusy(false); }
  };

  const deleteHh = async (id, name) => {
    if (!window.confirm(`Permanently delete household "${name}"? Members will lose IP-bypass.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/households/${id}`, { headers: auth });
      toast.success("Deleted");
      fetchHouseholds();
    } catch (err) { handleErr(err, "Delete failed"); }
    finally { setBusy(false); }
  };

  const addMember = async (id) => {
    if (!addMemberUsername.trim()) { toast.error("Enter a username"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/households/${id}/add-member`, { username: addMemberUsername.trim() }, { headers: auth });
      toast.success(`Added ${addMemberUsername.trim()}`);
      setAddMemberUsername(""); setAddMemberFor(null);
      fetchHouseholds();
    } catch (err) { handleErr(err, "Add member failed"); }
    finally { setBusy(false); }
  };

  const toggleMultiIp = async (id, next) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/households/${id}/multi-ip`, { allow: next }, { headers: auth });
      toast.success(r.data?.message || "Updated");
      fetchHouseholds();
    } catch (err) { handleErr(err, "Update failed"); }
    finally { setBusy(false); }
  };

  const forceLogout = async (householdId, memberId, username) => {
    if (!window.confirm(`Force ${username} to log out of their current session?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/households/${householdId}/force-logout/${memberId}`, {}, { headers: auth });
      toast.success(r.data?.message || "Logged out");
      fetchHouseholds();
    } catch (err) { handleErr(err, "Force logout failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 space-y-4" data-testid="admin-households-panel">
      <div className="flex items-center gap-2">
        <Home className="w-5 h-5 text-[#7B0099]" />
        <h3 className="font-bold text-sm">Households / Family Mode</h3>
        <button onClick={() => { fetchRequests(); fetchHouseholds(); }} className="retro-button text-xs px-2 py-1 ml-auto flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Pending Requests */}
      <div className="retro-window">
        <div className="bg-yellow-600 text-white px-3 py-1.5 font-bold text-xs">
          ⏳ Pending Requests ({requests.length})
        </div>
        <div className="p-2 bg-[#F4F4F4] max-h-60 overflow-y-auto retro-scrollbar">
          {loadingReq ? (
            <div className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : requests.length === 0 ? (
            <div className="text-xs text-gray-500 italic">No pending requests.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#E0E0E0]">
                <tr>
                  <th className="text-left p-1">Requester</th>
                  <th className="text-left p-1">Household Name</th>
                  <th className="text-left p-1">Reason</th>
                  <th className="text-left p-1">Date</th>
                  <th className="p-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.request_id} className="border-t border-gray-200" data-testid={`hh-request-${r.request_id}`}>
                    <td className="p-1 font-bold">{r.requester_username}</td>
                    <td className="p-1">{r.name}</td>
                    <td className="p-1 max-w-[200px] truncate" title={r.reason}>{r.reason || "—"}</td>
                    <td className="p-1 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="p-1 whitespace-nowrap">
                      <button onClick={() => approveRequest(r.request_id)} disabled={busy} className="retro-button text-[10px] px-1.5 py-0.5 bg-green-100 mr-1" data-testid={`approve-hh-${r.request_id}`}>
                        <Check className="w-3 h-3 inline" /> Approve
                      </button>
                      <button onClick={() => denyRequest(r.request_id)} disabled={busy} className="retro-button text-[10px] px-1.5 py-0.5 bg-red-100" data-testid={`deny-hh-${r.request_id}`}>
                        <X className="w-3 h-3 inline" /> Deny
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Direct create */}
      <div className="retro-window">
        <div className="bg-purple-700 text-white px-3 py-1.5 font-bold text-xs">➕ Create Household Directly</div>
        <div className="p-2 bg-[#F4F4F4] flex flex-wrap gap-1 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] block">Owner username</label>
            <input value={newOwner} onChange={e => setNewOwner(e.target.value)} className="retro-input w-full text-xs" placeholder="e.g. dad_smith" data-testid="hh-create-owner" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] block">Household name</label>
            <input value={newName} onChange={e => setNewName(e.target.value)} className="retro-input w-full text-xs" placeholder="The Smith Family" data-testid="hh-create-name" />
          </div>
          <button onClick={directCreate} disabled={busy} className="retro-button text-xs px-3 py-1" data-testid="hh-create-btn">Create</button>
        </div>
      </div>

      {/* All Households */}
      <div className="retro-window">
        <div className="bg-[#7B0099] text-white px-3 py-1.5 font-bold text-xs">
          🏠 All Households ({households.length})
        </div>
        <div className="p-2 bg-[#F4F4F4] max-h-96 overflow-y-auto retro-scrollbar space-y-2">
          {loadingHh ? (
            <div className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : households.length === 0 ? (
            <div className="text-xs text-gray-500 italic">No households yet.</div>
          ) : (
            households.map(h => (
              <div key={h.household_id} className="bg-white border border-gray-300 rounded p-2 text-xs" data-testid={`hh-${h.household_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{h.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${h.status === "active" ? "bg-green-100 text-green-700" : h.status === "suspended" ? "bg-orange-100 text-orange-700" : "bg-gray-100"}`}>
                    {h.status}
                  </span>
                  <span className="text-[10px] text-gray-600">
                    <Crown className="w-3 h-3 inline" /> {h.owner_username}
                  </span>
                  <span className="text-[10px] text-gray-600">
                    👥 {h.member_count}{h.max_members ? ` / ${h.max_members}` : " (unlimited)"}
                  </span>
                  {h.allow_multi_ip && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1" data-testid={`multi-ip-badge-${h.household_id}`}>
                      <Globe className="w-3 h-3" /> Multi-IP
                    </span>
                  )}
                  <code className="bg-purple-100 text-purple-800 text-[10px] px-2 py-0.5 rounded font-mono ml-auto">{h.invite_code}</code>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <button onClick={() => setAddMemberFor(addMemberFor === h.household_id ? null : h.household_id)} className="retro-button text-[10px] px-1.5 py-0.5">
                    <UserPlus className="w-3 h-3 inline" /> Add member
                  </button>
                  <button
                    onClick={() => toggleMultiIp(h.household_id, !h.allow_multi_ip)}
                    disabled={busy}
                    className={`retro-button text-[10px] px-1.5 py-0.5 ${h.allow_multi_ip ? "bg-blue-100 text-blue-700" : ""}`}
                    data-testid={`toggle-multi-ip-${h.household_id}`}
                    title="Allow this family to log in from more than one IP address at the same time"
                  >
                    <Globe className="w-3 h-3 inline" /> {h.allow_multi_ip ? "Multi-IP: ON" : "Multi-IP: OFF"}
                  </button>
                  {h.status === "active" ? (
                    <button onClick={() => suspend(h.household_id)} disabled={busy} className="retro-button text-[10px] px-1.5 py-0.5 bg-orange-100" data-testid={`suspend-${h.household_id}`}>
                      <Pause className="w-3 h-3 inline" /> Suspend
                    </button>
                  ) : (
                    <button onClick={() => reactivate(h.household_id)} disabled={busy} className="retro-button text-[10px] px-1.5 py-0.5 bg-green-100" data-testid={`reactivate-${h.household_id}`}>
                      <Play className="w-3 h-3 inline" /> Reactivate
                    </button>
                  )}
                  <button onClick={() => deleteHh(h.household_id, h.name)} disabled={busy} className="retro-button text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700" data-testid={`delete-${h.household_id}`}>
                    <Trash2 className="w-3 h-3 inline" /> Delete
                  </button>
                </div>
                {addMemberFor === h.household_id && (
                  <div className="mt-2 flex gap-1">
                    <input
                      value={addMemberUsername}
                      onChange={e => setAddMemberUsername(e.target.value)}
                      placeholder="Username to add"
                      className="retro-input flex-1 text-xs"
                      data-testid={`add-member-input-${h.household_id}`}
                    />
                    <button onClick={() => addMember(h.household_id)} disabled={busy} className="retro-button text-[10px] px-2 py-0.5" data-testid={`add-member-confirm-${h.household_id}`}>
                      Add
                    </button>
                    <button onClick={() => { setAddMemberFor(null); setAddMemberUsername(""); }} className="retro-button text-[10px] px-2 py-0.5">
                      Cancel
                    </button>
                  </div>
                )}
                <div className="mt-1 text-[10px] text-gray-500">
                  Created {new Date(h.created_at).toLocaleString()} · Approved by {h.approved_by || "—"}
                </div>

                {/* Live members & active IPs/devices */}
                <div className="mt-2 border-t border-gray-200 pt-2" data-testid={`hh-members-${h.household_id}`}>
                  <div className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-2">
                    <Globe className="w-3 h-3 text-blue-600" />
                    Members &amp; active devices
                    <span className="text-[9px] font-normal text-gray-500">
                      {h.online_count || 0} online · {h.active_ip_count || 0} active IP{(h.active_ip_count || 0) === 1 ? "" : "s"}
                    </span>
                  </div>
                  {(h.members || []).length === 0 ? (
                    <div className="text-[10px] text-gray-400 italic">No members yet.</div>
                  ) : (
                    <div className="space-y-0.5">
                      {(h.members || []).map(m => (
                        <div key={m.user_id} className="flex items-center gap-2 text-[10px]" data-testid={`hh-member-${m.user_id}`}>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.online ? "bg-green-500" : "bg-gray-300"}`} />
                          <span className="font-semibold truncate max-w-[110px]">
                            {m.username}{m.is_owner ? <Crown className="w-2.5 h-2.5 inline ml-0.5 text-yellow-600" /> : null}
                          </span>
                          {m.online ? (
                            <span className="text-blue-700 font-mono">
                              {m.ip || "—"}
                              {(m.city || m.country) && (
                                <span className="text-gray-500 font-sans"> · {[m.city, m.country].filter(Boolean).join(", ")}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              offline{m.last_seen ? ` · seen ${new Date(m.last_seen).toLocaleDateString()}` : ""}
                            </span>
                          )}
                          {m.online && (
                            <button
                              onClick={() => forceLogout(h.household_id, m.user_id, m.username)}
                              disabled={busy}
                              className="retro-button text-[9px] px-1.5 py-0.5 bg-red-100 text-red-700 ml-auto flex items-center gap-0.5"
                              data-testid={`force-logout-${m.user_id}`}
                              title="Force this session to log out"
                            >
                              <LogOut className="w-2.5 h-2.5" /> Log out
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHouseholdsPanel;
